/**
 * Heuristic checks for Colorado regulated marijuana delivery addresses under
 * **1 CCR 212-3 Rule 3-615(B)**: delivery to a **private residence** only — not schools,
 * higher-education campuses, public property, or commercial units (offices/retail).
 *
 * Automated checks are **best-effort** (free-text addresses); dispensary ops and drivers
 * remain responsible for final compliance. The Google Places lookup path on the
 * compliance endpoint catches institutional addresses by venue type when the client
 * supplies a `placeId` (web checkout). Mobile checkout sends no `placeId`, so this
 * regex is the only gate — keep it broad.
 */
/** Subset of issues we can detect from address strings without geocoding. */
export const COLORADO_PRIVATE_RESIDENCE_DELIVERY_REJECT_REASONS = [
    "out_of_state",
    "po_box",
    "school_or_campus_in_address",
    "commercial_mail_location",
];
function combineAddressLines(line1, line2) {
    return [line1, line2].filter((s) => s != null && String(s).trim().length > 0).join(" ");
}
/** PO Box / postal box patterns — not a deliverable private street address. */
function hasPoBoxPattern(text) {
    return (/\bP\.?\s*O\.?\s*BOX\b/i.test(text) ||
        /\bPOST\s+OFFICE\s+BOX\b/i.test(text) ||
        /\bPOSTAL\s+BOX\b/i.test(text));
}
/**
 * Trailing not-a-street-suffix guard. Used to allow streets named after
 * institutions (e.g. "100 High School Rd", "School District Way") while
 * rejecting an institutional name standing alone.
 */
const NOT_FOLLOWED_BY_STREET_SUFFIX = "(?!\\s+(?:RD|ROAD|DR|DRIVE|LN|LANE|WAY|CT|COURT|CIR|CIRCLE|BLVD|BOULEVARD|AVE|AVENUE|ST|STREET|PL|PLACE|PKWY|PARKWAY|TER|TERRACE|HWY|HIGHWAY)\\b)";
/**
 * School / campus / educational-institution phrases excluded under 3-615(B).
 *
 * Broad coverage — staging exposed addresses like "Lincoln Elementary",
 * "University of Colorado Denver", "Denver Academy" that the prior narrow
 * (literal "Elementary School" / "University Campus" only) regex missed.
 */
function hasSchoolOrCampusPattern(text) {
    // Educational institutional words. Each must have the trailing not-street-suffix
    // guard so addresses like "100 High School Rd" don't false-positive.
    const eduWords = [
        "ELEMENTARY",
        "MIDDLE\\s+SCHOOL",
        "HIGH\\s+SCHOOL",
        "PRESCHOOL",
        "PRE-?SCHOOL",
        "SCHOOLS?",
        "ACADEMY",
        "MONTESSORI",
        "CHARTER\\s+SCHOOL",
        "DAYCARE",
        "DAY\\s+CARE",
        "UNIVERSITY",
        "COLLEGE",
        "INSTITUTE\\s+OF\\s+TECHNOLOGY",
        "SEMINARY",
    ];
    for (const w of eduWords) {
        const re = new RegExp(`\\b${w}\\b${NOT_FOLLOWED_BY_STREET_SUFFIX}`, "i");
        if (re.test(text))
            return true;
    }
    // Standalone unguarded patterns (where the trailing-street-suffix guard is unhelpful).
    return /\bSCHOOL\s+DISTRICT\b/i.test(text) || /\bCAMPUS\b/i.test(text);
}
/**
 * Public / institutional / lodging / commercial venue phrases that aren't
 * private residences. Mapped to `school_or_campus_in_address` (institutional)
 * or `commercial_mail_location` (commercial) at the call site so the existing
 * union doesn't widen.
 *
 * Returns "institutional" / "commercial" / null.
 */
function classifyNonResidentialVenue(text) {
    const institutional = [
        "HOSPITAL",
        "MEDICAL\\s+CENTER",
        "URGENT\\s+CARE",
        "CLINIC",
        "LIBRARY",
        "COURTHOUSE",
        "CITY\\s+HALL",
        "TOWN\\s+HALL",
        "POLICE\\s+(?:DEPT|DEPARTMENT|STATION)",
        "FIRE\\s+(?:DEPT|DEPARTMENT|STATION)",
        "POST\\s+OFFICE",
        "PRISON",
        "JAIL",
        "DETENTION\\s+CENTER",
    ];
    for (const w of institutional) {
        const re = new RegExp(`\\b${w}\\b${NOT_FOLLOWED_BY_STREET_SUFFIX}`, "i");
        if (re.test(text))
            return "institutional";
    }
    const commercial = [
        "HOTEL",
        "MOTEL",
        "INN(?:\\s+OF)?",
        "RESORT",
        "HOSTEL",
        "LODGE",
        "OFFICE\\s+BUILDING",
        "OFFICE\\s+PARK",
        "SHOPPING\\s+CENTER",
        "SHOPPING\\s+MALL",
        "PLAZA",
    ];
    for (const w of commercial) {
        const re = new RegExp(`\\b${w}\\b${NOT_FOLLOWED_BY_STREET_SUFFIX}`, "i");
        if (re.test(text))
            return "commercial";
    }
    return null;
}
/** CMRA / mailbox-store style locations (not private residences). */
function hasCommercialMailPattern(text) {
    return (/\bPMB\b/i.test(text) ||
        /\bTHE\s+UPS\s+STORE\b/i.test(text) ||
        /\bUPS\s+STORE\b/i.test(text) ||
        /\bMAIL\s+BOXES\s+ETC\b/i.test(text) ||
        /\bMAIL\s+BOXES\b/i.test(text) ||
        /\bGENERAL\s+DELIVERY\b/i.test(text));
}
/**
 * Returns a rejection reason when the address fails automated private-residence checks,
 * or **`null`** when it passes.
 */
export function getColoradoPrivateResidenceDeliveryRejectReason(input) {
    const state = (input.deliveryState ?? "CO").trim().toUpperCase();
    if (state !== "CO") {
        return "out_of_state";
    }
    const text = combineAddressLines(input.deliveryAddressLine1, input.deliveryAddressLine2);
    if (text.trim().length === 0) {
        return null;
    }
    if (hasPoBoxPattern(text)) {
        return "po_box";
    }
    if (hasSchoolOrCampusPattern(text)) {
        return "school_or_campus_in_address";
    }
    const venueClass = classifyNonResidentialVenue(text);
    if (venueClass === "institutional") {
        // Hospitals / libraries / government buildings — same legal class as
        // schools (non-residential public/institutional venue). Reuse the
        // school reason so existing user-facing copy widens to cover both
        // (see `coloradoPrivateResidenceRejectMessage`).
        return "school_or_campus_in_address";
    }
    if (venueClass === "commercial") {
        return "commercial_mail_location";
    }
    if (hasCommercialMailPattern(text)) {
        return "commercial_mail_location";
    }
    return null;
}
//# sourceMappingURL=colorado-delivery-address.js.map